import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteContent } from '../../contexts/SiteContentContext';
import { CONTACT } from '../Layout';
import { Section, cx, useCopy } from '../core';
import contactHero from '../assets/img/contact-hero.webp';

// Values accepted by the /api/contact backend; partnership pages link with ?type=.
const TYPES = ['research', 'product', 'custom', 'investment', 'general'];
const TYPE_ALIASES = { industry: 'custom' };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const copy = {
  en: {
    kicker: 'CONTACT', title: <>Let’s build what<br />comes next.</>,
    intro: 'Tell us what you’re exploring. Whether you’re evaluating a product, developing an AI application, or looking for a research or business partner, we’d like to hear from you.',
    directTitle: 'Connect directly', directText: 'Reach the right person for your conversation.',
    researchKicker: 'Research collaboration', researchName: 'Jinhu Qi', researchText: 'AI agents, trustworthy AI, and academic collaboration.',
    businessKicker: 'Business & industry partnerships, Investment inquiries', businessName: 'Qiudan Wang',
    businessText: 'Product discussions, custom projects and industry partnerships, as well as strategic partnerships and investment discussions.',
    emailNote: 'Prefer email? Contact us directly using the addresses above.',
    formLabel: 'Contact form', formTitle: <>Start a<br />conversation.</>, required: 'All fields are required.',
    storage: 'Your submission is saved in private storage on our server for inquiry handling. It is not automatically sent by email. Please avoid sharing passwords or sensitive personal information.',
    name: 'Name', email: 'Email', organization: 'Company / organization', type: 'Inquiry type', message: 'Message',
    chooseType: 'Select an inquiry type',
    types: { research: 'Research collaboration', product: 'Product discussion', custom: 'Custom project / industry partnership', investment: 'Strategic partnership & investment', general: 'Other' },
    errors: { name: 'Please enter your name.', email: 'Please enter a valid email address.', organization: 'Please enter your company or organization.', type: 'Please choose an inquiry type.', message: 'Please add a short message.' },
    hint: 'Share your goals, the problem you’re solving, and any relevant timeline.',
    consentBefore: 'I agree to the ', privacy: 'Privacy Policy', consentAfter: ' and the use of these details to respond to my inquiry.',
    submit: 'Send inquiry', submitting: 'Saving inquiry…', savedButton: 'Inquiry saved',
    savedTag: 'SAVED', saved: 'Your inquiry has been saved.', savedDetail: 'For time-sensitive follow-up, please email the relevant contact and include your reference.', reference: 'Reference',
    failedTag: 'NOT SENT',
    failures: {
      error: 'We couldn’t save your inquiry. Your entered details are still here. Please try again or email us directly.',
      limited: 'Too many submission attempts. Please wait a few minutes or contact us by email.',
      invalid: 'Please check all required fields and accept the Privacy Policy before submitting.'
    },
    interest: (name) => `I would like to learn more about ${name}.`
  },
  zh: {
    kicker: '联系我们', title: <>一起构建<br />下一步。</>,
    intro: '告诉我们您正在探索的方向。无论是了解产品、开发 AI 应用，还是寻找科研或商业合作伙伴，我们都期待与您交流。',
    directTitle: '直接联系', directText: '根据您的需求，联系相应负责人。',
    researchKicker: '科研合作', researchName: '齐金虎 Jinhu Qi', researchText: 'AI 智能体、可信 AI 与学术合作。',
    businessKicker: '商务与产业合作、投资咨询', businessName: 'Qiudan Wang',
    businessText: '产品交流、定制项目与产业合作，以及战略合作与投资洽谈。',
    emailNote: '也欢迎直接使用以上邮箱与我们联系。',
    formLabel: '联系表单', formTitle: <>开启<br />对话。</>, required: '所有字段均为必填。',
    storage: '您的提交将保存在服务器的私有存储中，用于处理咨询，不会自动通过邮件发送。请勿提交密码或敏感个人信息。',
    name: '姓名', email: '邮箱', organization: '公司 / 机构', type: '咨询类型', message: '留言',
    chooseType: '请选择咨询类型',
    types: { research: '科研合作', product: '产品交流', custom: '定制项目 / 产业合作', investment: '战略合作与投资', general: '其他' },
    errors: { name: '请填写姓名。', email: '请填写有效的邮箱地址。', organization: '请填写公司或机构名称。', type: '请选择咨询类型。', message: '请简要填写留言。' },
    hint: '请简要介绍您的目标、需要解决的问题，以及相关时间安排。',
    consentBefore: '我同意', privacy: '隐私政策', consentAfter: '，并同意使用上述信息处理和回复本次咨询。',
    submit: '提交咨询', submitting: '正在保存…', savedButton: '咨询已保存',
    savedTag: '已保存', saved: '您的咨询已保存。', savedDetail: '如需及时跟进，请向对应联系人发送邮件，并附上咨询编号。', reference: '咨询编号',
    failedTag: '未发送',
    failures: {
      error: '未能保存您的咨询，已填写的内容仍保留在此页面。请重试或直接通过邮箱联系我们。',
      limited: '提交尝试过于频繁。请稍后再试，或直接通过邮箱联系我们。',
      invalid: '请检查所有必填信息，并同意隐私政策后再提交。'
    },
    interest: (name) => `我想进一步了解 ${name}。`
  }
};

const FIELDS = ['name', 'email', 'organization', 'type', 'message'];
const isValid = (field, value) => (field === 'email' ? EMAIL.test(value.trim()) : value.trim() !== '');

function ContactForm({ t }) {
  const location = useLocation();
  const { products } = useSiteContent();
  const query = new URLSearchParams(location.search);
  const requested = TYPE_ALIASES[query.get('type')] || query.get('type');
  const productName = products.find((product) => product.slug === query.get('product'))?.name || '';
  const [form, setForm] = useState(() => ({ name: '', email: '', organization: '', type: TYPES.includes(requested) ? requested : '', message: '', privacyAccepted: false, website: '' }));
  const [bad, setBad] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const refs = useRef({});
  const feedbackRef = useRef(null);
  const messageEdited = useRef(false);

  useEffect(() => { if (TYPES.includes(requested)) setForm((previous) => ({ ...previous, type: requested })); }, [requested]);
  useEffect(() => {
    if (!messageEdited.current) setForm((previous) => ({ ...previous, message: productName ? t.interest(productName) : '' }));
  }, [productName, t]);

  const check = (field, value = form[field]) => {
    const valid = field === 'privacyAccepted' ? value === true : isValid(field, value);
    setBad((previous) => ({ ...previous, [field]: !valid }));
    return valid;
  };
  const update = (event) => {
    const { name, value, type, checked } = event.target;
    const next = type === 'checkbox' ? checked : value;
    if (name === 'message') messageEdited.current = true;
    setForm((previous) => ({ ...previous, [name]: next }));
    if (bad[name]) check(name, next);
    if (feedback?.status === 'error') setFeedback(null);
  };
  const blur = (event) => { const { name, value } = event.target; if (bad[name] || value) check(name, value); };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting || feedback?.status === 'success') return;
    const results = Object.fromEntries([...FIELDS, 'privacyAccepted'].map((field) => [field, field === 'privacyAccepted' ? form.privacyAccepted : isValid(field, form[field])]));
    setBad(Object.fromEntries(Object.entries(results).map(([field, valid]) => [field, !valid])));
    const firstInvalid = [...FIELDS, 'privacyAccepted'].find((field) => !results[field]);
    if (firstInvalid) { setFeedback(null); refs.current[firstInvalid]?.focus(); return; }
    setSubmitting(true);
    setFeedback(null);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
        body: JSON.stringify(form), signal: controller.signal
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success !== true || typeof result.requestId !== 'string') {
        setFeedback({ status: 'error', kind: response.status === 429 ? 'limited' : response.status === 400 ? 'invalid' : 'error' });
      } else {
        setFeedback({ status: 'success', requestId: result.requestId });
      }
    } catch {
      setFeedback({ status: 'error', kind: 'error' });
    } finally {
      window.clearTimeout(timeout);
      setSubmitting(false);
      requestAnimationFrame(() => feedbackRef.current?.focus());
    }
  };

  const followUp = form.type === 'research' ? CONTACT.research : CONTACT.business;
  const field = (name, label, control, half) => (
    <div className={cx('fld', half && 'half', bad[name] && 'bad')}>
      <label htmlFor={`f-${name}`}>{label} <b aria-hidden="true">*</b></label>
      {control}
      <div className="ul" />
      <div className="err" id={`f-${name}-err`}><span>{bad[name] ? t.errors[name] : ''}</span></div>
      {name === 'message' && <p className="hint" id="f-message-hint">{t.hint}</p>}
    </div>
  );
  const common = (name) => ({
    id: `f-${name}`, name, value: form[name], onChange: update, onBlur: blur, required: true, disabled: submitting,
    ref: (node) => { refs.current[name] = node; }, 'aria-invalid': Boolean(bad[name]),
    'aria-describedby': cx(bad[name] && `f-${name}-err`, name === 'message' && 'f-message-hint') || undefined
  });

  return <form noValidate data-reveal="" style={{ '--i': 1 }} onSubmit={submit}>
    {field('name', t.name, <input {...common('name')} autoComplete="name" maxLength={100} />, true)}
    {field('email', t.email, <input {...common('email')} type="email" autoComplete="email" maxLength={254} />, true)}
    {field('organization', t.organization, <input {...common('organization')} autoComplete="organization" maxLength={180} />)}
    {field('type', t.type, <div className="sel">
      <select {...common('type')}>
        <option value="" disabled>{t.chooseType}</option>
        {TYPES.map((type) => <option key={type} value={type}>{t.types[type]}</option>)}
      </select>
      <svg className="chev" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
    </div>)}
    {field('message', t.message, <textarea {...common('message')} maxLength={5000} />)}
    <div className="trap" aria-hidden="true"><label htmlFor="f-website">Website</label><input id="f-website" name="website" value={form.website} onChange={update} tabIndex={-1} autoComplete="off" /></div>
    <label className={cx('consent', bad.privacyAccepted && 'bad')}>
      <input type="checkbox" id="f-ok" name="privacyAccepted" checked={form.privacyAccepted} onChange={update} required disabled={submitting}
        ref={(node) => { refs.current.privacyAccepted = node; }} aria-invalid={Boolean(bad.privacyAccepted)} />
      <span className="box"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 5 5 9-10" /></svg></span>
      <span>{t.consentBefore}<a href="/privacy" target="_blank" rel="noopener">{t.privacy}</a>{t.consentAfter}</span>
    </label>
    <div className="actions">
      <button className="btn btn-dark" type="submit" disabled={submitting || feedback?.status === 'success'} aria-busy={submitting}>
        {submitting ? t.submitting : feedback?.status === 'success' ? t.savedButton : t.submit} <span className="ar" aria-hidden="true">↗</span>
      </button>
    </div>
    <div className={cx('preview', feedback && 'on')}>
      <div>
        <div className={cx('box2', feedback?.status === 'error' && 'fail')} ref={feedbackRef} tabIndex={-1} role={feedback?.status === 'error' ? 'alert' : 'status'}>
          {feedback?.status === 'success' && <>
            <span className="pv">{t.savedTag}</span><b>{t.saved}</b> {t.savedDetail}
            <span className="ref">{t.reference}: <code>{feedback.requestId}</code> · <a href={`mailto:${followUp}?subject=${encodeURIComponent(`Website inquiry ${feedback.requestId}`)}`}>{followUp}</a></span>
          </>}
          {feedback?.status === 'error' && <><span className="pv">{t.failedTag}</span>{t.failures[feedback.kind]}</>}
        </div>
      </div>
    </div>
  </form>;
}

export default function ContactPage() {
  const { t } = useCopy(copy);
  return <>
    <section className="chero" aria-labelledby="h1">
      <div className="bg" aria-hidden="true"><img src={contactHero} alt="" /></div>
      <div className="wrap copy hin">
        <p className="k mono-k" style={{ '--i': 0 }}>{t.kicker}</p>
        <h1 id="h1" style={{ '--i': 1 }}>{t.title}</h1>
        <p className="intro" style={{ '--i': 2 }}>{t.intro}</p>
      </div>
    </section>
    <Section className="direct" id="direct">
      <div className="wrap">
        <div data-reveal="" style={{ '--i': 0 }}><h2>{t.directTitle}</h2><p className="sub">{t.directText}</p></div>
        <div className="contacts">
          <div className="ct" data-reveal="" style={{ '--i': 1 }}>
            <span className="ic"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 3h6M10 3v6L4.6 18.2A2 2 0 0 0 6.3 21h11.4a2 2 0 0 0 1.7-2.8L14 9V3" /><path d="M7 15h10" /></svg></span>
            <p className="k mono-k u">{t.researchKicker}</p>
            <h3>{t.researchName}</h3>
            <p>{t.researchText}</p>
            <a className="mlink" href={`mailto:${CONTACT.research}`}><u>{CONTACT.research}</u><span aria-hidden="true">↗</span></a>
          </div>
          <div className="ct" data-reveal="" style={{ '--i': 2 }}>
            <span className="ic"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18" /></svg></span>
            <p className="k mono-k">{t.businessKicker}</p>
            <h3>{t.businessName}</h3>
            <p>{t.businessText}</p>
            <a className="mlink" href={`mailto:${CONTACT.business}`}><u>{CONTACT.business}</u><span aria-hidden="true">↗</span></a>
          </div>
        </div>
        <p className="dfoot" data-reveal="" style={{ '--i': 3 }}><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>{t.emailNote}</p>
      </div>
    </Section>
    <Section className="cform" id="form">
      <div className="wrap">
        <div data-reveal="" style={{ '--i': 0 }}>
          <p className="lb2">{t.formLabel}</p>
          <h2>{t.formTitle}</h2>
          <p className="req">{t.required}</p>
          <p className="demo"><i><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg></i>{t.storage}</p>
        </div>
        <ContactForm t={t} />
      </div>
    </Section>
  </>;
}
